"use client"

import React, { ReactNode } from "react"
import { usePathname } from "next/navigation"
import { useSession } from "next-auth/react"

import { useAppContext } from "@app/components/AppContext"
import { DashboardSelect } from "@app/components/Dashboard/DashboardLayout/DashboardSelect"
import { useGreeting } from "@app/components/Dashboard/useGreeting"
import user from "@app/components/Dashboard/user.svg"
import Page from "@app/components/layout/Page"
import { cn } from "@app/utils"
import { UserImage } from "@app/components/UserImage"

const styles = {
  header: {
    wrapper:
      "flex gap-sm border-b border-b-gray-400 pb-[24px] pt-[12px] mb-[12px] flex-col md:flex-row md:items-center",
    pfp: "rounded-full bg-gray-200 min-w-[48px] min-h-[48px] w-[48px] h-[48px] inline-block",
    greeting: "text-[24px] md:text-[28px] lg:text-[32px] font-bold",
    subtext: "text-md",
  },
}

type DashboardLayoutProps = {
  children: ReactNode
  subtitle?: string
}

export const DashboardLayout = ({ children, subtitle }: DashboardLayoutProps) => {
  const { data: session } = useSession()
  const greeting = useGreeting(session?.user?.name)
  const pathname = usePathname()
  const { isCoolKids } = useAppContext()

  const dashboardSelectOptions = [
    { label: "Sales Activity Dashboard", value: "/dashboard/sales-activity" },
  ]

  // temp fix for urgent request
  const salesDashAuthedUsers = [
    "david.attardi@fsgi.com",
    "brannon.bourland@fsgi.com",
    "bobby.graham@fsgi.com",
    "aaron.davis@fsgi.com",
  ]

  //is user is dept 95 && div 99, add the sales overview link to the select options
  if (isCoolKids || salesDashAuthedUsers.includes(session.user.email))
    dashboardSelectOptions.push({
      label: "Sales Overview",
      value: "/dashboard/sales-overview",
    })

  dashboardSelectOptions.push({
    label: "Opportunity Calendar",
    value: "/dashboard/opportunity-calendar",
  })
  dashboardSelectOptions.push({
    label: "Marketing Summary",
    value: "/dashboard/marketing-summary",
  })

  return (
    <Page>
      {/* Header Section - (Was sticky but opp calendar page was breaking, so I'm removing the sticky for now) */}
      <section className={cn(styles.header.wrapper, "bg-white")}>
        <div className="flex items-center gap-sm">
          <div
            className={cn(styles.header.pfp, { "p-0": session.user.picture })}
          >
            <UserImage width={48} height={48} />
          </div>
          <div>
            <h2 className={styles.header.greeting}>{greeting}</h2>
            {subtitle !== undefined ? (
              <p className={styles.header.subtext}>{subtitle}</p>
            ) : (
              // This is a hacky fixto avoid layout shift
              <p
                aria-hidden="true"
                className={cn(
                  styles.header.subtext,
                  "invisible pointer-events-none"
                )}
              >
                Layout Fixture
              </p>
            )}
          </div>
        </div>
        <span className="ml-auto w-full md:w-[260px]">
          <DashboardSelect
            options={dashboardSelectOptions}
            pathname={pathname}
          />
        </span>
      </section>

      {/* Content Section - Scrollable */}
      <section>{children}</section>
    </Page>
  )
}
